import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCourses } from "../utils/wellspoken.api";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function CourseSection() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    fetchCourses()
      .then((data) => {
        if (isMounted) {
          setCourses(data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError("Unable to load courses at the moment.");
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleEnroll = (course) => {
    navigate("/contact", {
      state: { type: course.courseName },
    });
  };

  return (
    <main className="relative bg-gradient-to-b from-white via-blue-50 to-indigo-50 py-24 overflow-hidden">
      {/* ========== DECORATIVE BACKGROUND ========== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-200 to-purple-200 rounded-full filter blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-indigo-200 to-pink-200 rounded-full filter blur-3xl opacity-30" />
      </div>

      {/* ========== HEADER SECTION ========== */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-8 mb-20 text-center">
        <div className="inline-flex items-center gap-2 px-5 py-2 bg-white rounded-full shadow-lg border-2 border-blue-200 mb-8">
          <span className="text-2xl">📚</span>
          <span className="text-sm font-bold text-blue-600 uppercase tracking-wider">
            Our Courses
          </span>
        </div>

        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">
          Our English{" "}
          <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Courses
          </span>
        </h1>

        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Learn English with structured courses designed for real-world
          communication and career growth.
        </p>
      </section>

      {/* ========== COURSES CAROUSEL WITH SWIPER ========== */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {loading && (
          <div className="flex justify-center py-20">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce" />
              <div
                className="w-4 h-4 bg-indigo-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              />
              <div
                className="w-4 h-4 bg-purple-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="text-center py-20">
            <div className="inline-block px-8 py-4 bg-red-50 border-2 border-red-300 rounded-2xl">
              <p className="text-red-600 font-semibold">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && (
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            spaceBetween={20}
            slidesPerView={1}
            loop={true}
            autoplay={{ delay: 2500, disableOnInteraction: false }}

            pagination={{ clickable: true, el: ".course" }}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pb-8"
          >
            {courses.map((course) => (
              <SwiperSlide key={course.courseId}>
                <article className="group">
                  <div className="relative bg-white rounded-[2rem] p-8 pt-0 shadow-2xl border-4 border-blue-100 hover:border-blue-500 hover:shadow-blue-500/20 hover:-translate-y-3 transition-all duration-500">

                    {/* Integrated Top Bar */}
                    <div className="h-2 w-full rounded-t-2xl bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 mb-6" />

                    {/* Level Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold rounded-full mb-6 shadow-lg">
                      <span>🎓</span>
                      <span>{course.courseLevel}</span>
                    </div>

                    {/* Course Title */}
                    <h2 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                      {course.courseName}
                    </h2>

                    {/* Description */}
                    <ul className="space-y-3 mb-6">
                      {Array.isArray(course.courseDescription)
                        ? course.courseDescription.map((item, i) => (
                          <li key={i} className="flex gap-3 items-start">
                            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold text-sm mt-0.5">
                              ✓
                            </span>
                            <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                          </li>
                        ))
                        : course.courseDescription?.split("\n").map((item, i) => (
                          <li key={i} className="flex gap-3 items-start">
                            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold text-sm mt-0.5">
                              ✓
                            </span>
                            <span className="text-gray-700 text-sm leading-relaxed">{item.trim()}</span>
                          </li>
                        ))}
                    </ul>

                    {/* Meta Info */}
                    <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-100">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{course.courseDuration}</p>
                        <p className="text-xs text-gray-600 font-semibold uppercase">Months</p>
                      </div>
                      <div className="text-center border-l-2 border-blue-200">
                        <p className="text-sm font-bold text-gray-900">{course.courseMode}</p>
                        <p className="text-xs text-gray-600 font-semibold uppercase">Mode</p>
                      </div>
                    </div>

                    {/* Features */}
                    {Array.isArray(course.items) && course.items.length > 0 && (
                      <div className="mb-6 space-y-2">
                        {course.items.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex gap-2 items-center text-sm text-gray-700 bg-blue-50 rounded-xl px-3 py-2">
                            <span className="text-blue-500 font-bold">→</span>
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between gap-4 pt-6 border-t-2 border-gray-100">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">
                          Starting from
                        </p>
                        <p className="text-3xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                          ₹{course.price.toLocaleString("en-IN")}
                        </p>
                      </div>

                      <button
                        onClick={() => handleEnroll(course)}
                        className="
    relative
    px-8 py-3
    text-base font-semibold text-white
    rounded-full
    bg-[linear-gradient(to_bottom,#b30000_0%,#ff3b3b_45%,#d10000_55%,#7a0000_100%)]
    
    /* base depth */
    shadow-[0_8px_18px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.35)]
    
    /* smooth animation */
    transition-all duration-300 ease-out
    
    /* hover effects */
    hover:brightness-110
    hover:shadow-[0_12px_28px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.45)]
    hover:-translate-y-[2px]
    
    /* active press */
    active:translate-y-[1px]
    active:shadow-[0_6px_12px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.25)]
  "
                      >
                        Enroll
                        {/* <span className="text-lg">→</span> */}
                      </button>
                    </div>
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        <div className="course mt-3 flex justify-center" />
      </section>
    </main>
  );
}
